import React, { useEffect, useState } from "react";
import { dateOnly } from "@/lib/dates";
import { api } from "@/lib/api";
import { Plus, KanbanSquare, ChevronRight, ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";

const COLUMNS = [
  { key: "todo", label: "De făcut" },
  { key: "in_progress", label: "În lucru" },
  { key: "done", label: "Gata" },
];

export default function ProjectsView({ workspaceId, projects, users, currentUser, onProjectsChanged, subscribe }) {
  const [active, setActive] = useState(null);
  const [tasks, setTasks] = useState([]);

  // Create project dialog
  const [open, setOpen] = useState(false);
  const [pName, setPName] = useState("");
  const [pDesc, setPDesc] = useState("");

  const loadTasks = async (projectId) => {
    const r = await api.get(`/projects/${projectId}/tasks`);
    setTasks(r.data);
  };

  useEffect(() => {
    if (active) loadTasks(active.id);
  }, [active]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((ev) => {
      if (!active) return;
      if (ev.type === "task" && ev.data.project_id === active.id) {
        setTasks((prev) => prev.find((t) => t.id === ev.data.id) ? prev : [...prev, ev.data]);
      } else if (ev.type === "task_update" && ev.data.project_id === active.id) {
        setTasks((prev) => prev.map((t) => t.id === ev.data.id ? ev.data : t));
      } else if (ev.type === "task_delete") {
        setTasks((prev) => prev.filter((t) => t.id !== ev.data.id));
      }
    });
  }, [subscribe, active]);

  const createProject = async () => {
    if (!pName.trim()) return;
    try {
      await api.post("/projects", { name: pName, description: pDesc, workspace_id: workspaceId });
      setPName(""); setPDesc(""); setOpen(false);
      await onProjectsChanged();
      toast.success("Proiect creat");
    } catch {
      toast.error("Nu a reușit");
    }
  };

  if (!active) {
    return (
      <div className="h-full flex flex-col">
        <div className="h-14 px-6 flex items-center gap-3 border-b border-line/10 bg-bg justify-between">
          <div className="flex items-center gap-2">
            <KanbanSquare className="w-4 h-4 text-fg/50" />
            <h2 className="font-bold tracking-tight">Proiecte</h2>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-project-trigger" className="btn-primary flex items-center gap-1.5 text-sm">
                <Plus className="w-3.5 h-3.5" /> Proiect nou
              </button>
            </DialogTrigger>
            <DialogContent className="bg-surface border-line/10 text-fg rounded-none">
              <DialogHeader><DialogTitle className="font-bold tracking-tight">Proiect nou</DialogTitle></DialogHeader>
              <input data-testid="new-project-name-input" className="input-flat" placeholder="Numele proiectului" value={pName} onChange={(e) => setPName(e.target.value)} />
              <textarea data-testid="new-project-description-input" className="input-flat" placeholder="Descriere (opțional)" rows={3} value={pDesc} onChange={(e) => setPDesc(e.target.value)} />
              <DialogFooter><button data-testid="create-project-confirm-button" onClick={createProject} className="btn-primary">Creează</button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {projects.length === 0 ? (
            <div className="text-center text-fg/40 text-sm py-12">
              <p className="label-tiny mb-2">// NICIUN PROIECT</p>
              <p>Creează primul proiect ca să începi să urmărești munca.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.map((p) => (
                <button
                  key={p.id}
                  data-testid={`project-card-${p.id}`}
                  onClick={() => setActive(p)}
                  className="text-left bg-surface border border-line/10 p-4 hover:border-line/25 hover:-translate-y-0.5 transition-all group"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold tracking-tight">{p.name}</h3>
                    <ChevronRight className="w-4 h-4 text-fg/30 group-hover:text-fg/80 transition-colors" />
                  </div>
                  <p className="text-xs text-fg/50 line-clamp-2 min-h-[2rem]">{p.description || "Fără descriere"}</p>
                  <p className="label-tiny mt-3">{dateOnly(p.created_at)}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return <Board project={active} tasks={tasks} setTasks={setTasks} users={users} onBack={() => setActive(null)} />;
}

function Board({ project, tasks, setTasks, users, onBack }) {
  const [adding, setAdding] = useState(null); // column key
  const [title, setTitle] = useState("");
  const [dragId, setDragId] = useState(null);

  const addTask = async (status) => {
    if (!title.trim()) { setAdding(null); return; }
    try {
      const r = await api.post("/tasks", { project_id: project.id, title, status });
      setTasks((prev) => [...prev, r.data]);
      setTitle("");
      setAdding(null);
    } catch {
      toast.error("Nu a reușit");
    }
  };

  const move = async (taskId, status) => {
    setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status } : t));
    try { await api.patch(`/tasks/${taskId}`, { status }); }
    catch { toast.error("Mutarea nu a reușit"); }
  };

  const del = async (taskId) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    try { await api.delete(`/tasks/${taskId}`); }
    catch { toast.error("Nu a reușit"); }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="h-14 px-6 flex items-center gap-3 border-b border-line/10 bg-bg">
        <button data-testid="back-to-projects-button" onClick={onBack} className="text-fg/50 hover:text-fg"><ArrowLeft className="w-4 h-4" /></button>
        <KanbanSquare className="w-4 h-4 text-fg/50" />
        <h2 className="font-bold tracking-tight">{project.name}</h2>
        <span className="text-fg/40 text-xs truncate">{project.description}</span>
      </div>
      <div className="flex-1 overflow-x-auto p-6">
        <div className="grid grid-cols-3 gap-4 min-w-[800px] h-full">
          {COLUMNS.map((col) => (
            <div
              key={col.key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragId) { move(dragId, col.key); setDragId(null); } }}
              className="bg-bg border border-line/5 p-3 flex flex-col"
              data-testid={`column-${col.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="label-tiny">{col.label} · {tasks.filter((t) => t.status === col.key).length}</span>
                <button
                  data-testid={`add-task-${col.key}-button`}
                  onClick={() => { setAdding(col.key); setTitle(""); }}
                  className="text-fg/40 hover:text-fg"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-2 flex-1 overflow-y-auto">
                {tasks.filter((t) => t.status === col.key).map((t) => (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={() => setDragId(t.id)}
                    onDragEnd={() => setDragId(null)}
                    className="bg-surface-2 border border-line/10 p-3 cursor-grab active:cursor-grabbing hover:border-line/25 hover:-translate-y-0.5 transition-all group"
                    data-testid={`task-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm leading-snug flex-1">{t.title}</p>
                      <button
                        data-testid={`delete-task-${t.id}-button`}
                        onClick={() => del(t.id)}
                        className="opacity-0 group-hover:opacity-100 text-fg/40 hover:text-red-400 transition"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-[10px] text-fg/40 mt-2 mono">de {t.created_by_name}</p>
                  </div>
                ))}
                {adding === col.key && (
                  <div className="bg-surface-2 border border-brand/40 p-2">
                    <input
                      data-testid={`new-task-input-${col.key}`}
                      autoFocus
                      className="w-full bg-transparent text-sm outline-none"
                      placeholder="Titlul sarcinii…"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addTask(col.key);
                        if (e.key === "Escape") setAdding(null);
                      }}
                      onBlur={() => addTask(col.key)}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
